import React, { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material"
import { BaseProduct, Batch, batchService } from "services/batchService"
import { toast } from "react-toastify"
import { Formik, Form, Field, ErrorMessage } from "formik"
import * as Yup from "yup"

interface EditBatchDialogProps {
  openDialog: boolean
  onClose: () => void
  onConfirm: () => void
  selectedBatch: Batch | undefined | null
  selectedProduct: BaseProduct | undefined
}

const EditBatchDialog: React.FC<EditBatchDialogProps> = ({
  openDialog,
  onClose,
  onConfirm,
  selectedBatch,
  selectedProduct,
}) => {
  const [isSaving, setIsSaving] = useState(false)
  const [batchEdited, setBatchEdited] = useState({ batchId: 0, scheduledDate: "" })

  const validationSchema = Yup.object().shape({
    batchId: Yup.number()
      .required("O lote é obrigatório")
      .positive("O lote deve ser um número positivo")
      .integer("O lote deve ser um número inteiro"),
    scheduledDate: Yup.date().required("A data de entrega é obrigatória"),
  })

  const today = new Date()
  const todayDateString = today.toISOString().split("T")[0]

  const currentHour = today.getHours()
  const minSelectableDate =
    currentHour >= 7
      ? new Date(today.setDate(today.getDate() + 1)).toISOString().split("T")[0]
      : todayDateString

  const maxSelectableDate = currentHour < 7 ? todayDateString : undefined

  const handleScheduledDateBatchForm = (scheduledDate: string) => {
    setBatchEdited({ ...batchEdited, scheduledDate })
  }

  const handleBatchForm = (batchId: number) => {
    setBatchEdited({ ...batchEdited, batchId })
  }

  const handleClose = () => {
    onClose()
    setBatchEdited({
      batchId: 0,
      scheduledDate: "",
    })
  }

  const handleRequestEditBatch = async (values: { batchId: number; scheduledDate: string }) => {
    const updateData: any = {}
    if (values.batchId !== selectedBatch?.batchId) {
      updateData.batchIdCorrection = values.batchId
    }

    if (values.scheduledDate !== selectedBatch?.scheduledDate) {
      updateData.scheduledDateCorrection = values.scheduledDate
    }

    if (Object.keys(updateData).length > 0) {
      try {
        setIsSaving(true)
        await batchService.patch(selectedBatch?.id || 0, updateData)
        toast.success("Lote atualizado com sucesso!")
        onConfirm()
      } catch (error: any) {
        toast.error(error?.message ?? "Erro ao atualizar lote. Tente novamente.")
      } finally {
        setIsSaving(false)
      }
    } else {
      toast.info("Nenhuma alteração foi feita")
    }
  }

  return (
    <Dialog open={openDialog} onClose={() => !isSaving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>
        {selectedBatch?.batchId} - {selectedProduct?.productBaseName}
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            batchId: selectedBatch?.batchId || 0,
            scheduledDate: selectedBatch?.scheduledDate || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleRequestEditBatch}
        >
          {({ isSubmitting }) => (
            <Form>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
                <Field name="batchId">
                  {({ field, meta }: any) => (
                    <TextField
                      label="Lote"
                      {...field}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      InputLabelProps={{ style: { color: "#F8974C" } }}
                      fullWidth
                      disabled={isSaving}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
                <Field name="scheduledDate">
                  {({ field, meta }: any) => (
                    <TextField
                      label="Data de Entrega"
                      type="date"
                      {...field}
                      fullWidth
                      InputLabelProps={{ shrink: true, style: { color: "#F8974C" } }}
                      disabled={isSaving}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                      inputProps={{
                        min: minSelectableDate,
                      }}
                    />
                  )}
                </Field>
              </Box>
              <DialogActions>
                <Button onClick={onClose} sx={{ color: "text.primary" }} disabled={isSaving}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default EditBatchDialog
